/**
 * LikeButton — heart button for liking pplTalk messages and replies.
 *
 * Public visitors see a greyed-out heart with a "Log in to like" tooltip.
 * Logged-in users can toggle the like with optimistic UI updates.
 */

import React, { useState, useCallback } from 'react';
import { useAuthGate } from '../hooks/useAuthGate';
import { likeMessage, likeReply } from '../services/pplTalkService';

interface LikeButtonProps {
  /** The pplTalk message ID */
  messageId: string;
  /** If this is a reply like, provide the reply ID */
  replyId?: string;
  /** Current like count */
  likes: number;
  /** Array of user IDs who have liked */
  likedBy?: string[];
  /** Callback when like state changes (for parent state updates) */
  onLikeChange?: (newLikes: number, newLikedBy: string[]) => void;
  /** Size variant */
  size?: 'default' | 'small';
}

const LikeButton: React.FC<LikeButtonProps> = ({
  messageId,
  replyId,
  likes,
  likedBy = [],
  onLikeChange,
  size = 'default',
}) => {
  const { user, requireAuth } = useAuthGate();
  const [optimisticLikes, setOptimisticLikes] = useState<number | null>(null);
  const [optimisticLiked, setOptimisticLiked] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);

  const isLiked = optimisticLiked ?? (user ? likedBy.includes(user.uid) : false);
  const displayLikes = optimisticLikes ?? likes;

  const handleLike = useCallback(async () => {
    if (!user || loading) return;

    const action = isLiked ? 'remove' : 'add';
    const newLikes = isLiked ? Math.max(0, displayLikes - 1) : displayLikes + 1;
    const newLikedBy = isLiked
      ? likedBy.filter((id) => id !== user.uid)
      : [...likedBy, user.uid];

    // Optimistic update
    setOptimisticLikes(newLikes);
    setOptimisticLiked(!isLiked);
    setLoading(true);

    try {
      const result = replyId
        ? await likeReply(messageId, replyId, action)
        : await likeMessage(messageId, action);

      if (result.success) {
        onLikeChange?.(result.likes, result.likedBy);
        setOptimisticLikes(null);
        setOptimisticLiked(null);
      }
    } catch (err) {
      // Revert optimistic update
      console.error('Like failed:', err);
      setOptimisticLikes(null);
      setOptimisticLiked(null);
    } finally {
      setLoading(false);
    }
  }, [user, loading, isLiked, displayLikes, likedBy, messageId, replyId, onLikeChange]);

  const wrappedHandleLike = requireAuth(handleLike);

  return (
    <button
      className={`like-button ${isLiked ? 'liked' : ''} ${size === 'small' ? 'like-button--small' : ''} ${!user ? 'like-button--anonymous' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        wrappedHandleLike();
      }}
      disabled={loading}
      type="button"
      title={!user ? 'Log in to like' : isLiked ? 'Unlike' : 'Like'}
      aria-label={`${isLiked ? 'Unlike' : 'Like'} (${displayLikes} likes)`}
    >
      <span className="like-button__icon">
        {isLiked ? '❤️' : '🤍'}
      </span>
      <span className="like-button__count">{displayLikes}</span>
    </button>
  );
};

export default LikeButton;
